/* ==============================================================================
   Meus amigos
   ==============================================================================
   Quatro seções na mesma tela: pedidos recebidos, pedidos enviados, amigos,
   bloqueados. E a busca para adicionar.

   Por que tudo numa tela e não em abas
   ====================================
   O que a pessoa quer aqui é "quem me chamou?" e "cadê o João?". Com abas, a
   primeira pergunta exige lembrar em qual aba os pedidos ficam — e um pedido
   pendente não notificado é a coisa mais fácil de perder de vista. Numa tela
   só, pedidos recebidos ficam no topo e são impossíveis de não ver.

   O que a tela deliberadamente NÃO mostra
   =======================================
   Bloqueio que OUTRA pessoa aplicou. Se alguém te bloqueou, a relação
   simplesmente não aparece — a rota do servidor já filtra. Mostrar seria o
   convite à retaliação que o bloqueio existe para evitar.
   ============================================================================== */

import { useCallback, useEffect, useState } from 'react';
import { Avatar, useDialog } from '@concord/ui';
import { ApiError } from '@concord/core';

import type { ConcordApi, Friendship, UserSummary } from '../lib/concord.ts';

export interface FriendsScreenProps {
  api: ConcordApi;
  onOpenConversation: (userId: string) => void | Promise<void>;
  onFriendsChanged: () => void | Promise<void>;
}

export function FriendsScreen({
  api,
  onOpenConversation,
  onFriendsChanged,
}: FriendsScreenProps) {
  const { confirm } = useDialog();
  const [friendships, setFriendships] = useState<Friendship[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<UserSummary[]>([]);
  const [searching, setSearching] = useState(false);
  const [feedback, setFeedback] = useState<
    { kind: 'ok' | 'error'; message: string } | null
  >(null);

  const reload = useCallback(async () => {
    try {
      setFriendships(await api.listFriends());
    } catch {
      setFeedback({ kind: 'error', message: 'Não foi possível carregar seus amigos.' });
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void reload();
  }, [reload]);

  /*
   * Busca com debounce de 300 ms.
   *
   * Sem debounce, digitar "joao" dispara quatro requisições e a última pode
   * chegar antes da terceira — mostrando resultado de "joa" sobre o de "joao".
   * O `AbortController` cobre o resto: a requisição anterior é cancelada, então
   * nem chega a competir.
   */
  useEffect(() => {
    const term = query.trim();
    if (term.length < 3) {
      setResults([]);
      return;
    }

    const controller = new AbortController();
    const timer = setTimeout(() => {
      setSearching(true);
      void api.client
        .get<UserSummary[]>('/users/search', {
          query: { q: term },
          signal: controller.signal,
        })
        .then(setResults)
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 300);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query, api]);

  /*
   * A confirmacao se apaga em 6 segundos.
   *
   * Sem isso ela fica na tela pelo resto da sessao, e a proxima acao aparece
   * sobre uma mensagem antiga -- duas confirmacoes empilhadas, uma delas
   * mentindo. Erro NAO se apaga: se algo falhou, a pessoa precisa poder reler.
   */
  useEffect(() => {
    if (feedback === null || feedback.kind !== 'ok') return;
    const timer = setTimeout(() => setFeedback(null), 6000);
    return () => clearTimeout(timer);
  }, [feedback]);

  const act = useCallback(
    async (action: () => Promise<unknown>, successMessage: string) => {
      try {
        await action();
        setFeedback({ kind: 'ok', message: successMessage });
        await reload();
        await onFriendsChanged();

        /*
         * A busca e limpa depois de uma acao bem-sucedida.
         *
         * Com os resultados na tela, a lista de "Pedidos enviados" fica empurrada
         * para baixo da dobra -- exatamente onde a pessoa deveria olhar para ver
         * o que acabou de fazer. Limpar a busca colapsa os resultados e traz as
         * listas para cima.
         */
        setQuery('');
      } catch (error) {
        setFeedback({
          kind: 'error',
          message:
            error instanceof ApiError
              ? error.message
              : 'Não foi possível concluir a ação.',
        });
      }
    },
    [reload, onFriendsChanged],
  );

  const incoming = friendships.filter(
    (f) => f.state === 'pending' && f.direction === 'incoming',
  );
  const outgoing = friendships.filter(
    (f) => f.state === 'pending' && f.direction === 'outgoing',
  );
  const accepted = friendships.filter((f) => f.state === 'accepted');
  const blocked = friendships.filter((f) => f.state === 'blocked');

  return (
    <div className="h-full overflow-y-auto px-5 py-4">
      <header className="mb-5">
        <h1
          className="font-bold"
          style={{
            fontSize: 'var(--text-xl)',
            color: 'var(--color-text-primary)',
            letterSpacing: 'var(--tracking-tight)',
          }}
        >
          Amigos e pedidos
        </h1>
        <p
          className="mt-1"
          style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}
        >
          Separado dos membros de servidor: amizade não depende de compartilhar
          servidor nenhum.
        </p>
      </header>

      {/* ----------------------------------------------------------------------
          Procurar pessoas
          ----------------------------------------------------------------------
          Este campo BUSCA; o da lateral ENVIA. A diferença é a razão de os dois
          existirem, e o rótulo agora diz qual é qual.

          Na lateral se digita um nome de usuário que já se conhece — vindo de um
          convite, de um print, de outro app — e o pedido sai no Enter. Aqui se
          digita um pedaço do nome sem saber o resto, e o que volta é uma lista
          para escolher.

          Os dois se chamavam "Adicionar amigo", e nesse ponto eram dois campos
          de aparência igual, um acima do outro, com comportamentos diferentes no
          Enter. Um deles precisava dizer o que faz de diferente.
      ---------------------------------------------------------------------- */}
      <section className="mb-6">
        <label
          className="mb-1.5 block font-semibold uppercase"
          style={{
            fontSize: 'var(--text-2xs)',
            letterSpacing: 'var(--tracking-wide)',
            color: 'var(--color-text-tertiary)',
          }}
        >
          Procurar pessoas
        </label>
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Nome de usuário, ao menos 3 letras"
          className="w-full px-3 outline-none"
          style={{
            height: 36,
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-surface-sunken)',
            boxShadow: 'inset 0 1px 0 oklch(0% 0 0 / 0.24), 0 0 0 1px var(--color-border-default)',
            fontSize: 'var(--text-sm)',
            color: 'var(--color-text-primary)',
            maxWidth: 420,
          }}
        />

        {query.trim().length > 0 && query.trim().length < 3 && (
          <p
            className="mt-1.5"
            style={{ fontSize: 'var(--text-2xs)', color: 'var(--color-text-tertiary)' }}
          >
            Digite ao menos 3 letras. A busca é por início do nome — é o que impede
            listar a instância inteira.
          </p>
        )}

        {searching && (
          <p
            className="mt-2"
            style={{ fontSize: 'var(--text-2xs)', color: 'var(--color-text-tertiary)' }}
          >
            Buscando…
          </p>
        )}

        {/* ------------------------------------------------------------------
            Confirmacao, JUNTO da acao que a gerou
            ------------------------------------------------------------------
            Ela ficava depois de toda a secao de busca e antes das listas -- o
            que numa tela com resultados de busca abertos a empurrava para longe
            do botao clicado. O relato foi "nao mostrou o convite de amizade
            enviado": a acao acontecia, o pedido era criado, e a unica prova
            disso aparecia fora do campo de visao de quem acabou de clicar.

            Havia uma segunda causa, corrigida em outro lugar: o cliente da API
            era recriado a cada render do App (`onLogout` instavel), o que
            reexecutava os efeitos desta tela e podia descartar este estado.
        ------------------------------------------------------------------ */}
        {feedback !== null && (
          <p
            role="status"
            aria-live="polite"
            className="mt-2 flex items-start gap-2 px-3 py-2"
            style={{
              maxWidth: 420,
              borderRadius: 'var(--radius-md)',
              background:
                feedback.kind === 'ok'
                  ? 'var(--color-success-subtle)'
                  : 'var(--color-danger-subtle)',
              fontSize: 'var(--text-xs)',
              lineHeight: 'var(--leading-normal)',
              color:
                feedback.kind === 'ok'
                  ? 'var(--color-success)'
                  : 'var(--color-danger-on-subtle)',
            }}
          >
            <span aria-hidden="true" className="shrink-0 font-bold">
              {feedback.kind === 'ok' ? '\u2713' : '\u26a0'}
            </span>
            <span>{feedback.message}</span>
          </p>
        )}

        {results.length > 0 && (
          <ul className="mt-2 flex flex-col gap-1" style={{ maxWidth: 420 }}>
            {results.map((candidate) => {
              const existing = friendships.find((f) => f.user.id === candidate.id);
              return (
                <li key={candidate.id}>
                  <Row
                    user={candidate}
                    hint={
                      existing?.state === 'accepted'
                        ? 'já é seu amigo'
                        : existing?.state === 'pending'
                          ? 'pedido pendente'
                          : null
                    }
                    actions={
                      existing === undefined ? (
                        <Action
                          label="Adicionar"
                          onClick={() =>
                            void act(
                              () => api.addFriend(candidate.username),
                              /*
                               * A mensagem nomeia a pessoa e diz onde o pedido
                               * ficou.
                               *
                               * "Pedido enviado." nao permite verificar nada:
                               * nao diz para quem (numa lista de homonimos isso
                               * importa) nem onde procurar. Dizer "aparece em
                               * Pedidos enviados" transforma a confirmacao em
                               * instrucao -- a pessoa pode conferir.
                               */
                              `Pedido enviado para @${candidate.username}. ` +
                                'Ele aparece abaixo, em "Pedidos enviados", ' +
                                'ate ser aceito.',
                            )
                          }
                        />
                      ) : null
                    }
                  />
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {loading ? (
        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>
          Carregando…
        </p>
      ) : (
        <>
          {/* Pedidos recebidos PRIMEIRO: é a informação acionável da tela. */}
          <Section
            title={`Pedidos recebidos — ${incoming.length}`}
            empty="Nenhum pedido pendente."
            items={incoming}
            render={(friendship) => (
              <Row
                user={friendship.user}
                hint={friendship.note}
                actions={
                  <>
                    <Action
                      label="Aceitar"
                      primary
                      onClick={() =>
                        void act(
                          () => api.acceptFriend(friendship.user.id),
                          'Agora vocês são amigos.',
                        )
                      }
                    />
                    <Action
                      label="Recusar"
                      onClick={() =>
                        void act(
                          () => api.removeFriend(friendship.user.id),
                          'Pedido recusado.',
                        )
                      }
                    />
                  </>
                }
              />
            )}
          />

          <Section
            title={`Pedidos enviados — ${outgoing.length}`}
            empty="Nenhum pedido aguardando resposta."
            items={outgoing}
            render={(friendship) => (
              <Row
                user={friendship.user}
                hint="aguardando resposta"
                actions={
                  <Action
                    label="Cancelar"
                    onClick={() =>
                      void act(
                        () => api.removeFriend(friendship.user.id),
                        'Pedido cancelado.',
                      )
                    }
                  />
                }
              />
            )}
          />

          <Section
            title={`Amigos — ${accepted.length}`}
            empty="Você ainda não tem amigos adicionados."
            items={accepted}
            render={(friendship) => (
              <Row
                user={friendship.user}
                actions={
                  <>
                    <Action
                      label="Conversar"
                      primary
                      onClick={() => void onOpenConversation(friendship.user.id)}
                    />
                    <Action
                      label="Remover"
                      onClick={() =>
                        void act(
                          () => api.removeFriend(friendship.user.id),
                          'Amizade removida.',
                        )
                      }
                    />
                    <Action
                      label="Bloquear"
                      danger
                      onClick={async () => {
                        const proceed = await confirm({
                          title: `Bloquear ${friendship.user.username}?`,
                          description:
                            'Ela não será notificada e não poderá te enviar ' +
                            'mensagens. O histórico continua visível para os dois — ' +
                            'apagar destruiria a prova do que motivou o bloqueio.',
                          confirmLabel: 'Bloquear',
                          danger: true,
                        });
                        if (!proceed) return;
                        await act(
                          () => api.blockUser(friendship.user.id),
                          'Usuário bloqueado.',
                        );
                      }}
                    />
                  </>
                }
              />
            )}
          />

          {blocked.length > 0 && (
            <Section
              title={`Bloqueados — ${blocked.length}`}
              empty=""
              items={blocked}
              render={(friendship) => (
                <Row
                  user={friendship.user}
                  hint="bloqueado por você"
                  actions={
                    <Action
                      label="Desbloquear"
                      onClick={() =>
                        void act(
                          () => api.unblockUser(friendship.user.id),
                          'Usuário desbloqueado. A amizade anterior não é restaurada.',
                        )
                      }
                    />
                  }
                />
              )}
            />
          )}
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------------
   Peças
------------------------------------------------------------------------------ */

function Section<T>({
  title,
  empty,
  items,
  render,
}: {
  title: string;
  empty: string;
  items: T[];
  render: (item: T) => React.ReactNode;
}) {
  return (
    <section className="mb-6">
      <h2
        className="mb-2 font-semibold uppercase"
        style={{
          fontSize: 'var(--text-2xs)',
          letterSpacing: 'var(--tracking-wide)',
          color: 'var(--color-text-tertiary)',
        }}
      >
        {title}
      </h2>
      {items.length === 0 ? (
        empty.length > 0 && (
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>
            {empty}
          </p>
        )
      ) : (
        <ul className="flex flex-col gap-1" style={{ maxWidth: 560 }}>
          {items.map((item, index) => (
            <li key={index}>{render(item)}</li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Row({
  user,
  hint,
  actions,
}: {
  user: UserSummary;
  hint?: string | null;
  actions?: React.ReactNode;
}) {
  return (
    <div
      className="flex items-center gap-3 px-2.5 py-2"
      style={{
        borderRadius: 'var(--radius-md)',
        background: 'var(--color-surface-raised)',
      }}
    >
      <Avatar name={user.displayName ?? user.username} seed={user.id} size={32} status={user.status} />
      <div className="min-w-0 flex-1">
        <p
          className="truncate font-medium"
          style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-primary)' }}
        >
          {user.displayName ?? user.username}
        </p>
        <p
          className="truncate"
          style={{ fontSize: 'var(--text-2xs)', color: 'var(--color-text-tertiary)' }}
        >
          @{user.username}
          {hint != null && hint.length > 0 ? ` · ${hint}` : ''}
        </p>
      </div>
      {actions !== undefined && (
        <div className="flex shrink-0 items-center gap-1">{actions}</div>
      )}
    </div>
  );
}

function Action({
  label,
  onClick,
  primary = false,
  danger = false,
}: {
  label: string;
  onClick: () => void;
  primary?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="px-2.5 py-1 font-medium transition-colors"
      style={{
        borderRadius: 'var(--radius-md)',
        fontSize: 'var(--text-2xs)',
        background: primary
          ? 'var(--color-accent-solid)'
          : danger
            ? 'var(--color-danger-subtle)'
            : 'var(--color-surface-hover)',
        color: primary
          ? 'var(--color-on-solid)'
          : danger
            ? 'var(--color-danger-on-subtle)'
            : 'var(--color-text-secondary)',
      }}
    >
      {label}
    </button>
  );
}
